import { State } from '@meraki-internal/state';
import { ILink } from '../caremap/WithLinks';
import { IMemory, memoryMediaType } from './IMemory';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { isExpectedFetchError } from '../support/isExpectedFetchError';
import { CaremapMemoriesModelFactory } from './CaremapMemoriesModelFactory';

export type MemoryScope = 'care-recipient' | 'personal';

export class MemoriesModel extends State<Record<string, never>> {
    constructor(
        readonly scope: MemoryScope,

        // async because the personal scope resolves its link off the (cached) entry resource,
        // while the care-recipient scope resolves it off an already-in-hand caremap -- wrapping
        // both behind the same async accessor lets this one class serve either scope.
        private getLink: () => Promise<ILink | undefined>,
        private factory: CaremapMemoriesModelFactory
    ) {
        super({});
    }

    private memories: IMemory[] = [];

    // Whether the requester was ever granted the memories link. False for a care-recipient viewer
    // without get-private access (the api omits the link entirely rather than 403ing the list) --
    // distinct from "loaded but empty", which the UI should render differently (see MemoryList).
    private accessGranted = true;

    hasLoaded = false;

    // Set when the initial fetch failed, so the list/detail surfaces can render an inline retry
    // instead of silently hiding. Cleared at the start of each (re)load attempt via ensureLoaded.
    loadFailed = false;

    hasAccess = (): boolean => {
        return this.accessGranted;
    };

    // Load-on-demand guard so any consumer (the section list, or a directly-opened detail drawer on a
    // deep link) can trigger the initial fetch without duplicating the "already loaded" check. Callers
    // drive this from useAsyncEffect, which propagates a thrown rejection to the nearest React error
    // boundary -- that would crash the whole host (care profile / Account Settings modal) over a failed
    // memories fetch. Catch it, flag loadFailed, and log so the surface can render an inline retry
    // instead; hasLoaded stays false on failure, so retrying (or the next mount) re-attempts the fetch.
    ensureLoaded = async (): Promise<void> => {
        if (this.hasLoaded) {
            return;
        }

        this.loadFailed = false;
        this.setState({});
        try {
            await this.load();
        } catch (err: any) {
            this.loadFailed = true;
            this.setState({});
            this.factory.logger.error(`MemoriesModel.load failed to fetch memories: ${err.toString()}`);
        }
    };

    onWebsocketMessage = async (message: IResourceChangedSocketMessage) => {
        if (message.resourceChangeType !== 'changed') {
            return;
        }

        const link = await this.getLink();
        if (!link || message.resourceToRefresh.toLowerCase() !== link.href.toLowerCase()) {
            return;
        }

        try {
            await this.reconcile();
        } catch (err: any) {
            // a transient/aborted fetch is expected here — it retries on the next trigger.
            // Alert only on genuine failures.
            if (!isExpectedFetchError(err)) {
                this.factory.logger.error(`MemoriesModel.onWebsocketMessage failed reloading memories. Inner error: ${err.toString()}`);
            }
        }
    };

    load = async () => {
        const link = await this.getLink();

        if (!link) {
            this.memories = [];
            this.accessGranted = false;
            this.hasLoaded = true;
            this.setState({});
            return;
        }

        this.accessGranted = true;
        this.memories = await this.factory.apiClient.get(link, memoryMediaType);
        this.hasLoaded = true;
        this.setState({});
    };

    // Catch-up merge used by the foreground/reconnect reconciler (and by a websocket 'changed'
    // match): re-fetch the server's memories and converge the in-memory list by memoryId (update
    // changed in place, add new, drop removed) WITHOUT resetting hasLoaded, so an already-rendered
    // list never flashes a skeleton. Mirrors NotesModel.reconcile.
    reconcile = async (): Promise<void> => {
        const link = await this.getLink();
        if (!link) {
            return;
        }

        let server: IMemory[];
        try {
            server = await this.factory.apiClient.get(link, memoryMediaType);
        } catch (err: any) {
            // a transient/aborted memories fetch is expected for this best-effort catch-up — it
            // retries on the next trigger. Alert only on genuine failures.
            if (!isExpectedFetchError(err)) {
                this.factory.logger.error(`MemoriesModel.reconcile failed to fetch memories: ${err.toString()}`);
            }
            return;
        }

        const serverById = new Map(server.map(m => [m.memoryId, m]));
        const existingIds = new Set(this.memories.map(m => m.memoryId));
        const reconciled: IMemory[] = [];

        for (const existing of this.memories) {
            const updated = serverById.get(existing.memoryId);
            if (updated) {
                Object.assign(existing, updated);
                reconciled.push(existing);
            }
        }

        for (const serverMemory of server) {
            if (!existingIds.has(serverMemory.memoryId)) {
                reconciled.push(serverMemory);
            }
        }

        this.memories = reconciled;
        // A successful catch-up also recovers a failed initial load: mark loaded and clear the error
        // so a surface stuck on the inline retry shows the freshly-fetched data instead.
        this.hasLoaded = true;
        this.loadFailed = false;
        this.setState({});
    };

    getMemories = (): IMemory[] => {
        return this.memories;
    };

    // Mirrors NotesModel.deleteNote: the API call happens first, and the row only leaves the local
    // list once the server has confirmed the delete -- if it throws, the caller sees the error and
    // the list is left untouched (no rollback needed since nothing was mutated yet).
    deleteMemory = async (memory: IMemory) => {
        await this.factory.apiClient.delete(memory.links.delete, {});
        this.localDeleteMemory(memory);
    };

    localDeleteMemory = (memory: Pick<IMemory, 'memoryId'>) => {
        this.memories = this.memories.filter(m => m.memoryId !== memory.memoryId);
        this.setState({});
    };

    // Optimistic replace (mirrors InsightsModel's mutate-then-rollback): the fact updates locally
    // before the PUT resolves, so any other mounted view of this memory (e.g. MemoryList, still
    // rendered behind the drawer) reflects the edit instantly. The PUT actually supersedes the row
    // server-side under a brand-new memoryId and now returns that superseded item as its body (200,
    // memory.v1) -- on success we adopt it wholesale (new id, links, fields) instead of leaving the
    // edit under the OLD (now-dead) id. Leaving the old id in place let a later delete become a
    // server no-op on the dead row (the fact would silently reappear) and a second edit 404 (see the
    // 404 branch below). Returns the superseded item so callers that need the new identity (e.g. a
    // drawer keyed by memoryId) can follow it.
    editMemory = async (memory: IMemory, fact: string): Promise<IMemory | undefined> => {
        const previousFact = memory.fact;
        memory.fact = fact;
        this.setState({});

        let superseded: IMemory | undefined;
        try {
            superseded = await this.factory.apiClient.put(memory.links.edit, { fact });
        } catch (err: any) {
            if (err?.status === 404) {
                // superseded/deleted elsewhere since we loaded it (e.g. a concurrent edit already
                // consumed this memoryId) -- drop the stale row rather than resurrecting an edit
                // target the server no longer recognizes; reconcile will pick up the current state.
                this.localDeleteMemory(memory);
            } else {
                memory.fact = previousFact;
                this.setState({});
            }
            this.factory.logger.error(`MemoriesModel.editMemory failed for memoryId ${memory.memoryId}. Inner error: ${err.toString()}`);

            // unlike Insight's markSeen/dismiss (background, non-blocking), an edit is a deliberate
            // Save action the user is waiting on -- rethrow so the drawer can surface the failure
            // instead of silently reverting the field under them.
            throw err;
        }

        if (!superseded) {
            // Defensive: the edit endpoint is contracted to always return the superseded row's body
            // (200 + memory.v1 item, never 204). If that's ever violated, fall back to a full
            // reconcile rather than leaving the dead old-id row's now-invalid links in place.
            await this.reconcile();
            return undefined;
        }

        this.memories = this.memories.map(candidate => candidate.memoryId === memory.memoryId ? superseded! : candidate);
        this.setState({});
        return superseded;
    };
}
